// « Préparer » : tracer les pièces (polygone sommet par sommet, fermeture, édition des sommets) et les éléments d'implantation,
// compléter leurs données (« à saisir » en évidence) et voir ce qui empêche un calcul fiable.
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Check, MousePointer2, PenLine, Plus, Shapes, Trash2, Undo2, X } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { Alerte, Badge, Bouton, ChampChoix, Dialogue, LienBouton, Panneau, useConfirmation, useNotifier } from '../../../components/ui.tsx';
import { GEOMETRIE_ELEMENT, TYPES_ELEMENT, type PointControle, type Pt, type TypeElement } from '@shared/proposition/types.ts';
import { distancePointSegment } from '@shared/proposition/geometrie.ts';
import type { PlanVue } from '../modele.ts';
import { PlanCalque, type PlanCalquePoignee } from './PlanCalque.tsx';
import { FicheElement, FichePiece, type ElementSaisi } from './Fiches.tsx';
import {
  aireAffichee, centre, emprise, champsASaisir, consigneGeometrie, fermeLePolygone, LIBELLE_ELEMENT, LIBELLE_PLAFOND, LIBELLE_USAGE, nombre, pointsManquants, saisieVide, versSaisie,
  type ElementVue, type Manque, type PieceSaisie, type PieceVue,
} from './logique.ts';

type Outil = { type: 'consulter' } | { type: 'piece'; points: Pt[] } | { type: 'element'; te: TypeElement; points: Pt[] } | { type: 'sommets'; pieceId: string; points: Pt[]; choisi: number | null };
type Choix = { type: 'piece' | 'element'; id: string } | null;
const pts = (l: Pt[]) => l.map((q) => q.join(',')).join(' ');
const COULEUR_ELEMENT: Record<TypeElement, string> = {
  circulation: '#1F5FBF', zone_interdite: '#B42318', emplacement_ue: '#2E7D32', ue_imposee: '#2E7D32', passage_vertical: '#111827',
  evacuation_condensats: '#0B6FD6', tableau_electrique: '#C62828', point_gtb: '#5B3FBF', percement_interdit: '#B42318',
};
const LIBELLE_ATTRIBUT: Record<string, string> = {
  plenumMm: 'Plénum (mm)', plenumSource: 'Source du plénum', motif: 'Motif', support: 'Support', altitudeM: 'Altitude (m)', altitudeSource: 'Source de l’altitude',
  nature: 'Nature', repere: 'Repère', traversee: 'Traversée',
};
const COURT_ELEMENT: Record<TypeElement, string> = {
  circulation: 'Circulation', zone_interdite: 'Zone interdite', emplacement_ue: 'Emplacement UE', ue_imposee: 'UE imposée', passage_vertical: 'Passage',
  evacuation_condensats: 'Rejet condensats', tableau_electrique: 'Tableau', point_gtb: 'GTB', percement_interdit: 'Percement interdit',
};

export function Preparer({ dossierId, plans, pieces, elements, echelles, pointsEntrees, rafraichir }: {
  dossierId: string; plans: PlanVue[]; pieces: PieceVue[]; elements: ElementVue[]; echelles: Map<string, boolean>;
  pointsEntrees: PointControle[]; rafraichir: () => Promise<unknown>;
}) {
  const { peut } = useSession();
  const ecriture = peut('conception.ecrire');
  const notifier = useNotifier();
  const { confirmer, element: confirmation } = useConfirmation();
  const [params, setParams] = useSearchParams();
  const planId = plans.some((p) => p.id === params.get('plan')) ? params.get('plan')! : plans[0]?.id;
  const plan = plans.find((p) => p.id === planId);
  const vis = useRef<PlanCalquePoignee>(null);
  const [outil, setOutil] = useState<Outil>({ type: 'consulter' });
  const [choix, setChoix] = useState<Choix>(null);
  const [cible, setCible] = useState<{ x: number; y: number; jeton: number } | null>(null);
  const [fichePiece, setFichePiece] = useState<{ titre: string; saisie: PieceSaisie; piece: PieceVue | null } | null>(null);
  const [ficheElement, setFicheElement] = useState<{ saisie: ElementSaisi; element: ElementVue | null } | null>(null);
  const [choixType, setChoixType] = useState(false);
  const [typeChoisi, setTypeChoisi] = useState<TypeElement>('emplacement_ue');
  const glisse = useRef<number | null>(null);
  const zonePlan = useRef<HTMLDivElement>(null);
  // En tracé, le plan passe au premier plan : la page défile jusqu'à lui (la consigne reste collée en haut).
  const enTrace = outil.type !== 'consulter';
  useEffect(() => { if (enTrace) window.requestAnimationFrame(() => zonePlan.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })); }, [enTrace]);

  const echelle = plan?.echelle.statut === 'validee' && plan.echelle.mParUnite ? Number(plan.echelle.mParUnite) : null;
  const manques = useMemo(() => pointsManquants(plans.map((p) => ({ id: p.id, nom: p.nom, echelleValidee: echelles.get(p.id) ?? false })), pieces, elements), [plans, pieces, elements, echelles]);
  if (!plan) return null;
  const surPlan = pieces.filter((p) => p.planId === plan.id);
  const elementsPlan = elements.filter((e) => e.planId === plan.id);
  const pieceChoisie = choix?.type === 'piece' ? pieces.find((p) => p.id === choix.id) ?? null : null;
  const elementChoisi = choix?.type === 'element' ? elements.find((e) => e.id === choix.id) ?? null : null;

  const changerPlan = (id: string) => { const n = new URLSearchParams(params); n.set('plan', id); setParams(n, { replace: true }); setOutil({ type: 'consulter' }); setChoix(null); };
  const aller = (planCible: string | null, p?: Pt, c?: Choix) => {
    if (planCible && planCible !== plan.id) changerPlan(planCible);
    if (c) setChoix(c);
    if (p) setCible({ x: p[0], y: p[1], jeton: Date.now() });
  };
  const agir = async <T,>(fn: () => Promise<T>, succes?: string): Promise<T | undefined> => {
    try { const r = await fn(); await rafraichir(); if (succes) notifier(succes); return r; }
    catch (e) { notifier(messageErreur(e), true); return undefined; }
  };

  const poser = (p: Pt, u: number) => {
    if (outil.type === 'piece') {
      if (fermeLePolygone(outil.points, p, 18 * u)) { finirPiece(outil.points); return; }
      if (outil.points.length < 200) setOutil({ ...outil, points: [...outil.points, p] });
    } else if (outil.type === 'element') {
      const forme = GEOMETRIE_ELEMENT[outil.te];
      if (forme === 'point') { setFicheElement({ saisie: { type: outil.te, geometrie: [p], attributs: {} }, element: null }); return; }
      if (forme === 'polygone' && fermeLePolygone(outil.points, p, 18 * u)) { setFicheElement({ saisie: { type: outil.te, geometrie: outil.points, attributs: {} }, element: null }); return; }
      if (outil.points.length < 200) setOutil({ ...outil, points: [...outil.points, p] });
    } else if (outil.type === 'sommets') {
      // Un toucher sur le plan insère un sommet sur le côté le plus proche.
      const l = outil.points; let iMin = 0, dMin = Infinity;
      for (let i = 0; i < l.length; i++) { const d = distancePointSegment(p, l[i], l[(i + 1) % l.length]); if (d < dMin) { dMin = d; iMin = i; } }
      setOutil({ ...outil, points: [...l.slice(0, iMin + 1), p, ...l.slice(iMin + 1)], choisi: iMin + 1 });
    } else setChoix(null);
  };
  const finirPiece = (points: Pt[]) => setFichePiece({ titre: 'Nouvelle pièce', saisie: saisieVide(`Pièce ${surPlan.length + 1}`, points), piece: null });
  const finirElement = () => { if (outil.type === 'element') setFicheElement({ saisie: { type: outil.te, geometrie: outil.points, attributs: {} }, element: null }); };

  const enregistrerPiece = async (s: PieceSaisie) => {
    const existante = fichePiece?.piece;
    const r = await agir(() => existante ? api.put<PieceVue>(`/api/plans/${plan.id}/pieces/${existante.id}`, { ...s, majLe: existante.majLe })
      : api.post<PieceVue>(`/api/plans/${plan.id}/pieces`, s), existante ? 'Pièce enregistrée : validation à refaire.' : 'Pièce tracée.');
    if (r) { setOutil({ type: 'consulter' }); setChoix({ type: 'piece', id: r.id }); }
    return !!r;
  };
  const enregistrerElement = async (e: ElementSaisi) => {
    const existant = ficheElement?.element;
    const r = await agir(() => existant ? api.put<ElementVue>(`/api/plans/${plan.id}/elements/${existant.id}`, { ...e, majLe: existant.majLe })
      : api.post<ElementVue>(`/api/plans/${plan.id}/elements`, e), 'Élément enregistré.');
    if (r) { setOutil({ type: 'consulter' }); setChoix({ type: 'element', id: r.id }); }
    return !!r;
  };
  const enregistrerSommets = async () => {
    if (outil.type !== 'sommets') return;
    const p = pieces.find((x) => x.id === outil.pieceId); if (!p) return;
    const r = await agir(() => api.put(`/api/plans/${plan.id}/pieces/${p.id}`, { ...versSaisie(p, outil.points), majLe: p.majLe }), 'Contour enregistré : validation à refaire.');
    if (r) setOutil({ type: 'consulter' });
  };
  const supprimerPiece = async (p: PieceVue) => {
    if (!(await confirmer('Supprimer cette pièce ?', `« ${p.nom} » et ses données seront supprimées. Les propositions calculées deviendront périmées.`, 'Supprimer la pièce'))) return;
    if (await agir(() => api.del(`/api/plans/${plan.id}/pieces/${p.id}`), 'Pièce supprimée.')) setChoix(null);
  };
  const supprimerElement = async (e: ElementVue) => {
    if (!(await confirmer('Supprimer cet élément ?', `${LIBELLE_ELEMENT[e.type]} sera supprimé.`, 'Supprimer l’élément'))) return;
    if (await agir(() => api.del(`/api/plans/${plan.id}/elements/${e.id}`), 'Élément supprimé.')) setChoix(null);
  };

  // Poignées de sommets : glisser (souris ou doigt) ; le point suit le pointeur dans le repère du plan.
  const versPlanSvg = (e: PointerEvent<SVGElement>): Pt | null => {
    const svg = (e.currentTarget as SVGElement).ownerSVGElement; if (!svg) return null;
    const r = svg.getBoundingClientRect(); const vb = svg.viewBox.baseVal;
    return [Math.round(((e.clientX - r.left) / r.width) * vb.width * 100) / 100, Math.round(((e.clientY - r.top) / r.height) * vb.height * 100) / 100];
  };

  const enCours = outil.type === 'piece' || outil.type === 'element' ? outil.points : [];
  const consigne = outil.type === 'piece' ? `Touchez le plan à chaque angle de la pièce (${outil.points.length} sommet(s)) ; touchez le premier sommet ou « Fermer la pièce » pour terminer.${aireAffichee(outil.points, echelle) != null ? ` Surface : ${nombre(aireAffichee(outil.points, echelle), 1)} m².` : ''}`
    : outil.type === 'element' ? `${LIBELLE_ELEMENT[outil.te]} : ${consigneGeometrie(outil.te)} (${outil.points.length} point(s))`
    : outil.type === 'sommets' ? 'Glissez un sommet pour le déplacer ; touchez le plan pour ajouter un sommet sur le côté le plus proche.' : null;

  const calque = (u: number) => (
    <>
      {elementsPlan.map((e) => {
        const c = COULEUR_ELEMENT[e.type]; const sel = elementChoisi?.id === e.id; const forme = GEOMETRIE_ELEMENT[e.type];
        const [x, y] = centre(e.geometrie);
        return (
          <g key={e.id} className="prp-element" role="button" tabIndex={0} aria-label={LIBELLE_ELEMENT[e.type]}
            onClick={(ev) => { if (outil.type !== 'consulter') return; ev.stopPropagation(); setChoix({ type: 'element', id: e.id }); }}
            onKeyDown={(ev) => { if (ev.key === 'Enter') setChoix({ type: 'element', id: e.id }); }}>
            {forme === 'polygone' && <polygon points={pts(e.geometrie)} fill={e.type === 'zone_interdite' ? 'rgba(180,35,24,0.12)' : 'rgba(31,95,191,0.06)'} stroke={c} strokeWidth={(sel ? 4 : 2) * u} strokeDasharray={`${8 * u} ${5 * u}`} />}
            {forme === 'polyligne' && <><polyline points={pts(e.geometrie)} fill="none" stroke="transparent" strokeWidth={18 * u} /><polyline points={pts(e.geometrie)} fill="none" stroke={c} strokeWidth={(sel ? 7 : 5) * u} /></>}
            {forme === 'point' && <><circle cx={x} cy={y} r={20 * u} fill="transparent" /><circle cx={x} cy={y} r={(sel ? 10 : 8) * u} fill={c} stroke="#FFFFFF" strokeWidth={2 * u} /></>}
            <text x={x + (forme === 'point' ? 12 * u : 0)} y={y + (forme === 'point' ? 4 * u : 0)} fontSize={11 * u} className="prp-texte-plan" fill={c} fontWeight={600}
              textAnchor={forme === 'point' ? 'start' : 'middle'}>{COURT_ELEMENT[e.type]}</text>
          </g>
        );
      })}
      {surPlan.map((p) => {
        const edition = outil.type === 'sommets' && outil.pieceId === p.id;
        const poly = edition ? outil.points : p.polygone;
        const sel = pieceChoisie?.id === p.id; const manque = champsASaisir(p).length > 0;
        const [x, y] = centre(poly);
        return (
          <g key={p.id} className="prp-piece" role="button" tabIndex={0} aria-label={`${p.nom}${manque ? ', données à saisir' : ''}`}
            onClick={(ev) => { if (outil.type !== 'consulter') return; ev.stopPropagation(); setChoix({ type: 'piece', id: p.id }); }}
            onKeyDown={(ev) => { if (ev.key === 'Enter') setChoix({ type: 'piece', id: p.id }); }}>
            <polygon points={pts(poly)} fill={p.usage === 'non_traite' ? 'rgba(91,102,118,0.12)' : sel ? 'rgba(0,127,131,0.16)' : 'rgba(0,127,131,0.06)'}
              stroke={manque ? '#8A4B00' : '#00565A'} strokeWidth={(sel || edition ? 4 : 2) * u} strokeDasharray={manque ? `${8 * u} ${5 * u}` : undefined} />
            <text x={x} y={y - 4 * u} fontSize={13 * u} textAnchor="middle" fontWeight={600} className="prp-texte-plan">{p.nom}</text>
            <text x={x} y={y + 12 * u} fontSize={11 * u} textAnchor="middle" className="prp-texte-plan" fill={manque ? '#8A4B00' : '#3B4656'}>
              {manque ? `à saisir : ${champsASaisir(p).length}` : p.surfaceM2 != null ? `${nombre(p.surfaceM2, 1)} m²` : ''}
            </text>
            {edition && outil.points.map((q, i) => (
              <g key={i} className="prp-poignee" role="button" tabIndex={0} aria-label={`Sommet ${i + 1}`} style={{ touchAction: 'none' }}
                onPointerDown={(ev) => { ev.stopPropagation(); (ev.currentTarget as Element).setPointerCapture(ev.pointerId); glisse.current = i; setOutil({ ...outil, choisi: i }); }}
                onPointerMove={(ev) => { if (glisse.current !== i) return; const np = versPlanSvg(ev); if (np) setOutil((o) => (o.type === 'sommets' ? { ...o, points: o.points.map((r, j) => (j === i ? np : r)) } : o)); }}
                onPointerUp={() => { glisse.current = null; }} onPointerCancel={() => { glisse.current = null; }} onClick={(ev) => ev.stopPropagation()}>
                <circle cx={q[0]} cy={q[1]} r={22 * u} fill="transparent" />
                <circle cx={q[0]} cy={q[1]} r={(outil.choisi === i ? 9 : 7) * u} fill={outil.choisi === i ? '#007F83' : '#FFFFFF'} stroke="#00565A" strokeWidth={2.5 * u} />
              </g>
            ))}
          </g>
        );
      })}
      {enCours.length > 0 && (
        <g className="prp-brouillon" pointerEvents="none">
          <polyline points={pts(enCours)} fill="none" stroke="#0B5FFF" strokeWidth={3 * u} strokeDasharray={`${8 * u} ${5 * u}`} />
          {enCours.map((q, i) => <circle key={i} cx={q[0]} cy={q[1]} r={(i === 0 && enCours.length >= 3 ? 11 : 6) * u} fill={i === 0 ? '#0B5FFF' : '#FFFFFF'} stroke="#0B5FFF" strokeWidth={2.5 * u} />)}
        </g>
      )}
    </>
  );

  const passages = elements.filter((e) => e.type === 'passage_vertical' && e.planId !== plan.id)
    .map((e) => ({ id: e.id, libelle: `${plans.find((p) => p.id === e.planId)?.nom ?? 'Autre plan'} — ${e.attributs.nature ?? 'passage'}` }));
  const echelleValidee = echelles.get(plan.id) ?? false;
  const principalTracer = ecriture && !peut('proposition.calculer');

  return (
    <div className="pile">
      {outil.type === 'consulter' ? (
        <div className="cch-barre" role="toolbar" aria-label="Préparer le plan">
          {plans.length > 1 && <div className="prp-choix-plan"><ChampChoix libelle="Plan" value={plan.id} onChange={(e) => changerPlan(e.target.value)} options={plans.map((p) => ({ valeur: p.id, libelle: p.nom }))} /></div>}
          {ecriture && <Bouton variante={principalTracer ? 'principal' : 'secondaire'} icone={<PenLine aria-hidden />} disabled={!echelleValidee}
            onClick={() => { setOutil({ type: 'piece', points: [] }); setChoix(null); }}>Tracer une pièce</Bouton>}
          {ecriture && <Bouton icone={<Shapes aria-hidden />} aria-label="Ajouter un élément" disabled={!echelleValidee} onClick={() => setChoixType(true)}><span className="masque-etroit">Ajouter un élément</span><span className="visible-etroit">Élément</span></Bouton>}
        </div>
      ) : (
        <div className="cch-consigne pile prp-consigne" role="group" aria-label="Tracé en cours">
          <p role="status">{consigne}</p>
          <div className="cch-barre">
            {outil.type === 'piece' && <Bouton variante="principal" icone={<Check aria-hidden />} disabled={outil.points.length < 3} onClick={() => finirPiece(outil.points)}>Fermer la pièce</Bouton>}
            {outil.type === 'element' && GEOMETRIE_ELEMENT[outil.te] !== 'point' && <Bouton variante="principal" icone={<Check aria-hidden />}
              disabled={outil.points.length < (GEOMETRIE_ELEMENT[outil.te] === 'polygone' ? 3 : 2)} onClick={finirElement}>Terminer</Bouton>}
            {outil.type === 'sommets' && <Bouton variante="principal" icone={<Check aria-hidden />} onClick={enregistrerSommets}>Enregistrer le contour</Bouton>}
            {(outil.type === 'piece' || outil.type === 'element') && <Bouton icone={<Undo2 aria-hidden />} disabled={!outil.points.length} onClick={() => setOutil({ ...outil, points: outil.points.slice(0, -1) })}>Retirer le dernier point</Bouton>}
            {outil.type === 'sommets' && <Bouton icone={<Trash2 aria-hidden />} disabled={outil.choisi === null || outil.points.length <= 3}
              onClick={() => setOutil({ ...outil, points: outil.points.filter((_, i) => i !== outil.choisi), choisi: null })}>Supprimer le sommet choisi</Bouton>}
            <Bouton icone={<X aria-hidden />} onClick={() => setOutil({ type: 'consulter' })}>Annuler</Bouton>
          </div>
        </div>
      )}
      {!echelleValidee && (
        <Alerte ton="ambre" titre="Échelle du plan à valider" action={<LienBouton to={`/etudes/${dossierId}/conception?vue=cheminements&plan=${plan.id}`}>Ouvrir les cheminements</LienBouton>}>
          Le tracé des pièces et le calcul exigent une échelle validée (même règle que le métré). Calibrez et faites valider l’échelle dans l’onglet Cheminements.
        </Alerte>
      )}

      <div className="prp-disposition">
        <div ref={zonePlan} className="prp-zone-plan">
        <PlanCalque ref={vis} plan={plan} calque={calque} emprise={emprise([...surPlan.map((p) => p.polygone), ...elementsPlan.map((e) => e.geometrie)])} poser={outil.type === 'consulter' ? undefined : poser} cible={cible}
          libelle={`Plan ${plan.nom} : pièces et éléments d’implantation`} />
        </div>
        <aside className="pile prp-cote" aria-label="Pièce ou élément choisi et points à compléter">
          {pieceChoisie && (
            <Panneau titre={pieceChoisie.nom} actions={pieceChoisie.validee ? <Badge ton="succes">Validée</Badge> : <Badge ton="ambre">À valider</Badge>}>
              <FichePieceResume p={pieceChoisie} />
              {ecriture && outil.type === 'consulter' && (
                <div className="groupe-btn" style={{ marginTop: 12 }}>
                  <Bouton compact onClick={() => setFichePiece({ titre: `Données — ${pieceChoisie.nom}`, saisie: versSaisie(pieceChoisie), piece: pieceChoisie })}>Modifier les données</Bouton>
                  <Bouton compact icone={<MousePointer2 aria-hidden />} onClick={() => setOutil({ type: 'sommets', pieceId: pieceChoisie.id, points: pieceChoisie.polygone, choisi: null })}>Modifier les sommets</Bouton>
                  {peut('echelle.valider') && !pieceChoisie.validee && <Bouton compact icone={<Check aria-hidden />} disabled={!pieceChoisie.lieuId}
                    title={!pieceChoisie.lieuId ? 'Rattachez d’abord la pièce à une zone.' : undefined}
                    onClick={() => agir(() => api.post(`/api/plans/${plan.id}/pieces/${pieceChoisie.id}/validation`, { majLe: pieceChoisie.majLe }), 'Pièce validée.')}>Valider la pièce</Bouton>}
                  <Bouton compact variante="danger" icone={<Trash2 aria-hidden />} onClick={() => supprimerPiece(pieceChoisie)}>Supprimer</Bouton>
                </div>
              )}
            </Panneau>
          )}
          {elementChoisi && (
            <Panneau titre={LIBELLE_ELEMENT[elementChoisi.type]}>
              <dl className="fiche cch-fiche-1">
                {Object.entries(elementChoisi.attributs).filter(([k, v]) => v != null && v !== '' && k !== 'lienId').map(([k, v]) => <div key={k}><dt>{LIBELLE_ATTRIBUT[k] ?? k}</dt><dd>{k === 'traversee' ? (v === 'avec_calfeutrement' ? 'avec calfeutrement' : 'interdite') : String(v).replace('.', ',')}</dd></div>)}
                {elementChoisi.type === 'passage_vertical' && <div><dt>Relié à</dt><dd>{elementChoisi.attributs.lienId ? plans.find((p) => elements.some((e) => e.id === elementChoisi.attributs.lienId && e.planId === p.id))?.nom ?? 'autre plan' : <span className="cch-texte-ambre">à relier</span>}</dd></div>}
              </dl>
              {ecriture && outil.type === 'consulter' && (
                <div className="groupe-btn" style={{ marginTop: 12 }}>
                  <Bouton compact onClick={() => setFicheElement({ saisie: { type: elementChoisi.type, geometrie: elementChoisi.geometrie, attributs: elementChoisi.attributs }, element: elementChoisi })}>Modifier</Bouton>
                  <Bouton compact variante="danger" icone={<Trash2 aria-hidden />} onClick={() => supprimerElement(elementChoisi)}>Supprimer</Bouton>
                </div>
              )}
            </Panneau>
          )}
          <ListeManques manques={manques} pointsEntrees={pointsEntrees}
            aller={(m) => {
              const pc = m.pieceId ? pieces.find((x) => x.id === m.pieceId) : null; const el = m.elementId ? elements.find((x) => x.id === m.elementId) : null;
              aller(m.planId, pc ? centre(pc.polygone) : el ? centre(el.geometrie) : undefined, pc ? { type: 'piece', id: pc.id } : el ? { type: 'element', id: el.id } : undefined);
            }} />
        </aside>
      </div>

      <section className="pile" aria-labelledby="prp-pieces-titre" style={{ gap: 8 }}>
        <h3 id="prp-pieces-titre" className="cch-titre-tableau">Pièces du plan <span className="compteur">{surPlan.length}</span></h3>
        {!surPlan.length ? <p className="petit discret">Aucune pièce tracée sur ce plan.{ecriture ? ' « Tracer une pièce », puis touchez chaque angle.' : ''}</p> : (
          <ul className="prp-liste-pieces">
            {surPlan.map((p) => {
              const m = champsASaisir(p);
              return (
                <li key={p.id}>
                  <button type="button" className={`prp-piece-btn ${pieceChoisie?.id === p.id ? 'est-choisi' : ''}`} onClick={() => aller(null, centre(p.polygone), { type: 'piece', id: p.id })}>
                    <span className="prp-piece-nom">{p.nom}</span>
                    <span className="tres-petit secondaire">{LIBELLE_USAGE[p.usage]} · {p.surfaceM2 != null ? `${nombre(p.surfaceM2, 1)} m²` : 'surface à calculer'}</span>
                    <span>{m.length ? <Badge ton="ambre">{m.length} à saisir</Badge> : p.validee ? <Badge ton="succes">Validée</Badge> : <Badge ton="neutre">À valider</Badge>}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialogue titre="Ajouter un élément d’implantation" ouvert={choixType} fermer={() => setChoixType(false)}
        pied={<><Bouton onClick={() => setChoixType(false)}>Annuler</Bouton><Bouton variante="principal" icone={<Plus aria-hidden />}
          onClick={() => { setChoixType(false); setChoix(null); setOutil({ type: 'element', te: typeChoisi, points: [] }); }}>Tracer sur le plan</Bouton></>}>
        <div className="pile">
          <ChampChoix libelle="Type d’élément" value={typeChoisi} onChange={(e) => setTypeChoisi(e.target.value as TypeElement)}
            options={TYPES_ELEMENT.map((t) => ({ valeur: t, libelle: LIBELLE_ELEMENT[t] }))} aide={consigneGeometrie(typeChoisi)} />
        </div>
      </Dialogue>
      <FichePiece ouvert={!!fichePiece} titre={fichePiece?.titre ?? ''} depart={fichePiece?.saisie ?? null} planId={plan.id} echelle={echelle}
        enregistrer={enregistrerPiece} fermer={() => setFichePiece(null)} />
      <FicheElement ouvert={!!ficheElement} depart={ficheElement?.saisie ?? null} passages={passages} enregistrer={enregistrerElement} fermer={() => setFicheElement(null)} />
      {confirmation}
    </div>
  );
}

function FichePieceResume({ p }: { p: PieceVue }) {
  const m = new Set(champsASaisir(p));
  const v = (cle: string, texte: string | null) => (m.has(cle) ? <span className="cch-texte-ambre">à saisir</span> : texte ?? <span className="discret">—</span>);
  return (
    <dl className="fiche">
      <div><dt>Usage</dt><dd>{LIBELLE_USAGE[p.usage]}</dd></div>
      <div><dt>Surface</dt><dd>{v('surface', p.surfaceM2 != null ? `${nombre(p.surfaceM2, 1)} m²${p.surfaceReleveeM2 != null ? ' (relevée)' : ''}` : null)}</dd></div>
      {p.usage !== 'non_traite' && <>
        <div><dt>Hauteur sous plafond</dt><dd>{v('hauteur', p.hauteurSousPlafondM != null ? `${nombre(p.hauteurSousPlafondM, 2)} m` : null)}</dd></div>
        <div><dt>Plafond</dt><dd>{v('plafond', p.plafond ? LIBELLE_PLAFOND[p.plafond] : null)}</dd></div>
        <div><dt>Plénum</dt><dd>{v('plenum', p.plenumMm != null ? `${nombre(p.plenumMm)} mm` : null)}</dd></div>
        <div><dt>Occupants</dt><dd>{v('occupants', p.occupants != null ? nombre(p.occupants) : null)}</dd></div>
      </>}
      <div><dt>Zone</dt><dd>{v('lieuId', p.lieuId ? 'rattachée' : null)}</dd></div>
      {p.validation && <div><dt>Validée par</dt><dd>{p.validation.par}</dd></div>}
    </dl>
  );
}

function ListeManques({ manques, pointsEntrees, aller }: { manques: Manque[]; pointsEntrees: PointControle[]; aller: (m: Manque) => void }) {
  const total = manques.length + pointsEntrees.length;
  return (
    <Panneau titre={`Avant de calculer${total ? ` (${total})` : ''}`}>
      {!total ? <p className="petit"><Check size={16} aria-hidden /> Aucune donnée manquante relevée : le calcul peut être lancé.</p> : (
        <ul className="prp-manques">
          {manques.map((m) => (
            <li key={m.cle}>
              <button type="button" className="prp-manque-btn" onClick={() => aller(m)}>
                <Badge ton={m.niveau === 'bloquant' ? 'danger' : m.niveau === 'a_saisir' ? 'ambre' : 'neutre'}>{m.niveau === 'bloquant' ? 'Bloquant' : m.niveau === 'a_saisir' ? 'À saisir' : 'À valider'}</Badge>
                <span className="petit">{m.message}</span>
              </button>
            </li>
          ))}
          {pointsEntrees.map((p) => (
            <li key={p.id} className="prp-manque-btn prp-manque-serveur">
              <Badge ton={p.niveau === 'bloquant' ? 'danger' : 'ambre'}>Dernier calcul</Badge><span className="petit">{p.message}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="tres-petit discret" style={{ marginTop: 8 }}>« Bloquant » : le calcul est refusé. « À saisir » : le calcul passe mais signale des points « à relever ». « À valider » : proposition calculée pour information, non reprenable.</p>
    </Panneau>
  );
}
